import * as React from 'react';
import { EmojiData } from './emoji.data';
import { TextBubble } from './_components/text-bubble/text-bubble';

interface EmojiObject {
  value: string;
  canAddTone?: boolean;
  canAddGender?: boolean;
  canAddProfession?: boolean;
}
interface EmojiDict {
  [propertyName: string]: EmojiObject;
}

enum EmojiSkinTones {
  Light = 'Light',
  MediumLight = 'MediumLight',
  Medium = 'Medium',
  MediumDark = 'MediumDark',
  Dark = 'Dark'
}

interface EmojiSkinToneLookup {
  Light: string;
  MediumLight: string;
  Medium: string;
  MediumDark: string;
  Dark: string;
}

enum EmojiProfessions {
  HealthWorker = 'HealthWorker',
  Student = 'Student',
  Teacher = 'Teacher',
  Judge = 'Judge',
  Cook = 'Cook',
  Mechanic = 'Mechanic',
  FactoryWorker = 'FactoryWorker',
  OfficeWorker = 'OfficeWorker',
  Scientist = 'Scientist',
  Technologist = 'Technologist',
  Singer = 'Singer',
  Artist = 'Artist',
  Pilot = 'Pilot',
  Astronaut = 'Astronaut',
  Firefighter = 'Firefighter'
}

interface EmojiProfessionLookup {
  HealthWorker: string;
  Student: string;
  Teacher: string;
  Judge: string;
  Cook: string;
  Mechanic: string;
  FactoryWorker: string;
  OfficeWorker: string;
  Scientist: string;
  Technologist: string;
  Singer: string;
  Artist: string;
  Pilot: string;
  Astronaut: string;
  Firefighter: string;
}

enum Gender {
  Neutral = 'Neutral',
  Female = 'Female',
  Male = 'Male'
}

interface EmojiProperties {
  name?: EmojiObject;
  gender?: Gender;
  skinTone?: string;
  size?: number;
  profession?: string;
  text?: string;
}

export class Emoji extends React.Component<EmojiProperties> {
  public static readonly EmojiNames: EmojiDict = EmojiData;

  public static readonly EmojiGenders = Gender;

  public static readonly SkinTones: EmojiSkinToneLookup = {
    [EmojiSkinTones.Light]: String.fromCodePoint(0x1f3fb),
    [EmojiSkinTones.MediumLight]: String.fromCodePoint(0x1f3fc),
    [EmojiSkinTones.Medium]: String.fromCodePoint(0x1f3fd),
    [EmojiSkinTones.MediumDark]: String.fromCodePoint(0x1f3fe),
    [EmojiSkinTones.Dark]: String.fromCodePoint(0x1f3ff)
  };

  public static readonly Professions: EmojiProfessionLookup = {
    [EmojiProfessions.HealthWorker]: Emoji.EmojiNames.StaffAsch.value,
    [EmojiProfessions.Student]: Emoji.EmojiNames.GradCap.value,
    [EmojiProfessions.Teacher]: Emoji.EmojiNames.School.value,
    [EmojiProfessions.Judge]: Emoji.EmojiNames.Wheat.value,
    [EmojiProfessions.Cook]: Emoji.EmojiNames.FryingPan.value,
    [EmojiProfessions.Mechanic]: Emoji.EmojiNames.Wrench.value,
    [EmojiProfessions.FactoryWorker]: Emoji.EmojiNames.Factory.value,
    [EmojiProfessions.OfficeWorker]: Emoji.EmojiNames.Briefcase.value,
    [EmojiProfessions.Scientist]: Emoji.EmojiNames.Microscope.value,
    [EmojiProfessions.Technologist]: Emoji.EmojiNames.Computer.value,
    [EmojiProfessions.Singer]: Emoji.EmojiNames.Microphone.value,
    [EmojiProfessions.Artist]: Emoji.EmojiNames.ArtistPalette.value,
    [EmojiProfessions.Pilot]: Emoji.EmojiNames.Airplane.value,
    [EmojiProfessions.Astronaut]: Emoji.EmojiNames.Rocket.value,
    [EmojiProfessions.Firefighter]: Emoji.EmojiNames.Firetruck.value
  };

  private static readonly ZERO_WIDTH_JOINER = String.fromCodePoint(0x200d);
  private static readonly MALE = String.fromCodePoint(0x2642);
  private static readonly FEMALE = String.fromCodePoint(0x2640);
  private emoji: EmojiObject;

  private static getRandomProperty(obj: Object): string {
    const randomPropName = Object.keys(obj)[Math.round(Math.random() * (Object.keys(obj).length - 1))];

    return randomPropName;
  }

  private static getRandomEmoji(): EmojiObject {
    const randomEmojiName = Emoji.getRandomProperty(Emoji.EmojiNames);

    return Emoji.EmojiNames[randomEmojiName];
  }

  constructor(props: EmojiProperties) {
    super(props);
    this.emoji = this.props.name ? this.props.name : Emoji.getRandomEmoji();
    if (this.emoji.canAddTone) {
      this.applyTone();
    }

    if (this.emoji.canAddGender) {
      this.applyGender();
    }

    if (this.emoji.canAddProfession) {
      this.applyProfession();
    }
  }

  render() {
    return (
      <span style={{ position: 'relative' }}>
        <span style={{ fontSize: this.props.size ? this.props.size.toString() + 'px' : '50px' }}>
          {this.emoji!.value}
        </span>
        {this.props.text && (
          <div
            style={{
              position: 'absolute',
              top: this.props.size ? (-this.props.size).toString() + 'px' : '-75px',
              left: this.props.size ? (this.props.size * 0.8).toString() + 'px' : '100px',
              width: this.props.size ? (this.props.size * 0.9).toString() + 'px' : '100px',
              height: this.props.size ? (this.props.size * 1.4).toString() + 'px' : '100px',
              zIndex: 12
            }}
          >
            <TextBubble text={this.props.text} />
          </div>
        )}
      </span>
    );
  }

  private applyGender() {
    let newEmoji = { ...this.emoji };

    if (this.emoji!.canAddGender && this.props.gender === Gender.Male) {
      newEmoji.value += Emoji.ZERO_WIDTH_JOINER + Emoji.MALE;
    } else if (this.emoji!.canAddGender && this.props.gender === Gender.Female) {
      newEmoji.value += Emoji.ZERO_WIDTH_JOINER + Emoji.FEMALE;
    } else {
      const prob = Math.random();
      if (prob <= 0.33) {
        newEmoji.value += Emoji.ZERO_WIDTH_JOINER + Emoji.FEMALE;
      } else if (prob > 0.3 && prob <= 0.66) {
        newEmoji.value += Emoji.ZERO_WIDTH_JOINER + Emoji.MALE;
      }
    }

    this.emoji = newEmoji;
  }

  private applyTone() {
    let newEmoji = { ...this.emoji };
    if (this.props.skinTone) {
      newEmoji.value += this.props.skinTone;
    } else if (this.emoji!.canAddTone) {
      const randomToneName = Emoji.getRandomProperty(Emoji.SkinTones);
      newEmoji.value += Emoji.SkinTones[randomToneName];
    }

    this.emoji = newEmoji;
  }

  private applyProfession() {
    let newEmoji = { ...this.emoji };
    if (this.props.profession) {
      newEmoji.value += this.props.profession;
    } else if (this.emoji.canAddProfession) {
      const randomProfName = Emoji.getRandomProperty(Emoji.Professions);
      newEmoji.value += Emoji.ZERO_WIDTH_JOINER + Emoji.Professions[randomProfName];
    }

    this.emoji = newEmoji;
  }
}



// WEBPACK FOOTER //
// ./src/common/components/emoji/Emoji.tsx